import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

class RecommendationService {
  /**
   * Create a new recommendation action
   * @param {string} accessToken - JWT access token
   * @param {string} auditPlanId - Audit plan ID
   * @param {string} sectionId - Section ID
   * @param {string} questionId - Question ID
   * @param {Object} recommendationData - The recommendation data
   */
  async createRecommendation(accessToken, auditPlanId, sectionId, questionId, recommendationData) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(recommendationData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Recommendation creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Fetch all recommendations for a specific question with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {string} auditPlanId - Audit plan ID
   * @param {string} sectionId - Section ID
   * @param {string} questionId - Question ID
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getRecommendations(accessToken, auditPlanId, sectionId, questionId, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.owner) {
        queryParams.append('owner', filters.owner);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch recommendations',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get a specific recommendation by ID
   * @param {string} accessToken - JWT access token
   * @param {string} auditPlanId - Audit plan ID
   * @param {string} sectionId - Section ID
   * @param {string} questionId - Question ID
   * @param {number} recommendationId - The recommendation ID
   */
  async getRecommendationDetail(accessToken, auditPlanId, sectionId, questionId, recommendationId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/${recommendationId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch recommendation details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Update a recommendation
   * @param {string} accessToken - JWT access token
   * @param {string} auditPlanId - Audit plan ID
   * @param {string} sectionId - Section ID
   * @param {string} questionId - Question ID
   * @param {number} recommendationId - The recommendation ID
   * @param {Object} recommendationData - Updated recommendation data
   */
  async updateRecommendation(accessToken, auditPlanId, sectionId, questionId, recommendationId, recommendationData) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/questions/${questionId}/recommendations/${recommendationId}/update/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(recommendationData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Recommendation update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Format recommendation data for API submission
   * @param {Object} formData - Raw form data
   * @returns {Object} Formatted data for API
   */
  formatRecommendationData(formData) {
    return {
      question_recommendation_id: formData.question_recommendation,
      recommendation_owner: formData.recommendation_owner,
      delay_of_recommendation: formData.delay_of_recommendation,
      elements_of_recommendation: formData.elements_of_recommendation,
      kpi_of_recommendation: formData.kpi_of_recommendation,
    };
  }

  /**
   * Get follow-ups for a recommendation (for managers)
   * @param {string} accessToken - JWT access token
   * @param {number} recommendationId - The recommendation ID
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getRecommendationFollowUpsForManager(accessToken, recommendationId, filters = {}, paginationData = {}) {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/question-recommendations/${recommendationId}/followups${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch follow-ups',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get follow-up details for a recommendation (for managers)
   * @param {string} accessToken - JWT access token
   * @param {number} recommendationId - The recommendation ID
   * @param {number} followupId - The follow-up ID
   */
  async getFollowUpDetailForManager(accessToken, recommendationId, followupId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/question-recommendations/${recommendationId}/followups/${followupId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch follow-up details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }





  
}

const recommendationService = new RecommendationService();
export default recommendationService;